import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";

/**
 * Picking one visit out of a candidacy's history.
 *
 * The reads behind this screen used to hand back a single open appointment, so
 * the first item in the list was the only item. They now return every
 * appointment a candidacy ever had, cancelled ones included, oldest first. The
 * first item is therefore the oldest, and reaching for it means acting on a
 * visit that happened weeks ago.
 *
 * Which visit a screen wants is a question with three different answers, so
 * each one is named here rather than guessed at the call site.
 */

/**
 * Which visit actions a row may offer.
 *
 * Two different questions, kept apart because they have different answers
 * (DEV-3237).
 *
 * Booking and rescheduling are bounded by the server: POST
 * /cronofy/properties/:id/book-visit requires the candidate to stand on
 * Invited or Visitor and refuses the broker anywhere else with "Not invited to
 * schedule visit". Offering them elsewhere would offer something that cannot
 * happen.
 *
 * Cancelling is not a booking and the server does not bound it that way.
 * Rule 1 lets a candidacy with an appointment still ahead move forward to
 * `candidateTenant` and `tenant`, and from either of those the move to `notEligible` is blocked until the appointment is called off. Gating cancel on
 * the same two steps as booking therefore left the broker with a move they
 * were told to make and no way to make it. So cancel follows the appointment,
 * not the tab.
 */
export interface IVisitActionContext {
  step: EIntakeStep;
  archivedAt?: Date | string | null;
  hasCalendarSync: boolean;
  /** An appointment that still stands, whether or not it has taken place. */
  hasVisit: boolean;
  /** An appointment still to come. Only one of those can be moved. */
  hasUpcomingVisit: boolean;
}

/** The two steps the server books a visit for. */
const BOOKABLE_STEPS: EIntakeStep[] = [
  EIntakeStep.invited,
  EIntakeStep.visitor,
];

export const canBookOrRescheduleVisit = (context: IVisitActionContext) =>
  context.hasCalendarSync &&
  !context.archivedAt &&
  BOOKABLE_STEPS.includes(context.step);

/**
 * Calling off stays available on an appointment that already took place,
 * because a broker has to be able to undo one booked by mistake, and because
 * cancelling is also what clears it out of their own calendar. Only the
 * candidate loses this once their visit is over.
 *
 * An archived candidacy is left out, as it always was. Its appointment does
 * stay in the broker's calendar, which is a real gap, but it is not this one:
 * taking it back out of the archive gives the menu back, so
 * nobody is stuck.
 */
export const canCancelVisit = (context: IVisitActionContext) =>
  context.hasCalendarSync && !context.archivedAt && context.hasVisit;

export interface IRawVisit {
  id: string;
  applicationId: string | number;
  status?: string;
  /** Not cancelled and not over yet, as the server judged it. */
  isUpcoming?: boolean;
  start?: string;
  end?: string;
  slotStart?: string;
  slotEnd?: string;
}

const CANCELLED = "cancelled";

/** Both field names, because the two reads behind this screen disagree. */
export const getVisitStart = (visit: IRawVisit) =>
  visit.start || visit.slotStart;

export const getVisitEnd = (visit: IRawVisit) => visit.end || visit.slotEnd;

const isFor = (visit: IRawVisit, applicationId: string) =>
  String(visit.applicationId) === String(applicationId);

/**
 * Still to come: not cancelled, and not over.
 *
 * The server states this on isUpcoming and that answer is taken as given, so
 * the two sides cannot come to disagree about what "still ahead" means. It is
 * worked out here only for a response that does not carry the flag, and then by
 * the same rule the server uses: measured against the end, so a visit under way
 * still counts. It is in the broker's calendar and can still be called off.
 */
export const isVisitUpcoming = (visit: IRawVisit, now: Date) => {
  if (visit.status === CANCELLED) {
    return false;
  }

  if (typeof visit.isUpcoming === "boolean") {
    return visit.isUpcoming;
  }

  const end = getVisitEnd(visit);

  return !!end && new Date(end).getTime() > now.getTime();
};

/**
 * The appointment a broker can still move or call off. At most one exists: the
 * backend allows a candidacy only one open appointment at a time.
 */
export const getUpcomingVisit = (
  visits: IRawVisit[],
  applicationId: string,
  now: Date,
) =>
  visits.find(
    visit => isFor(visit, applicationId) && isVisitUpcoming(visit, now),
  );

const getLatest = (visits: IRawVisit[]) =>
  visits.reduce<IRawVisit | undefined>((latest, visit) => {
    if (!latest) {
      return visit;
    }

    const at = new Date(getVisitStart(visit) || 0).getTime();
    const latestAt = new Date(getVisitStart(latest) || 0).getTime();

    return at > latestAt ? visit : latest;
  }, undefined);

/**
 * The most recent appointment that still stands, whether or not it has already
 * taken place. This is what a label reads: a visit that happened is a fact
 * about this candidacy long after there is anything left to cancel.
 *
 * Read by picking the latest rather than the last in the list, because sorted
 * input is a promise made by a response and not by this function.
 */
export const getLastStandingVisit = (
  visits: IRawVisit[],
  applicationId: string,
) =>
  getLatest(
    visits.filter(
      visit => isFor(visit, applicationId) && visit.status !== CANCELLED,
    ),
  );

/**
 * Whether the last thing that happened to this candidacy's appointments was a
 * cancellation. Not who cancelled: that is recorded nowhere, and a broker who
 * cancelled it themselves sees the same thing.
 */
export const isLastVisitCancelled = (
  visits: IRawVisit[],
  applicationId: string,
) =>
  getLatest(visits.filter(visit => isFor(visit, applicationId)))?.status ===
  CANCELLED;
